import React from 'react';
import PropTypes from 'prop-types';

import copySrc from '../../assets/images/copy.svg';
import downloadSrc from '../../assets/images/download.svg';
import paidSrc from '../../assets/images/paid.svg';
import pdfSrc from '../../assets/images/pdf.svg';
import successSrc from '../../assets/images/success.svg';

// The decorative icons, drawn from the SVG's URL with <img> rather than inlined
// as a component.
//
// They used to be imported as `./icon.svg?react`, which only becomes a React
// component if the bundler is told what that query means - @svgr/webpack, via
// the resourceQuery rule in webpack.config.js. Any other toolchain resolves the
// same import to the emitted file's URL instead, so `<PaidSVG/>` turns into
// `createElement('/static/media/paid.<hash>.svg')` and React throws
// InvalidCharacterError, taking the whole route down with it. That is not
// hypothetical: it is what a Create React App build of this source does, and
// what the order page was failing with.
//
// A plain `import src from './icon.svg'` is the one spelling every bundler
// agrees on - CRA, webpack, Vite, Parcel all hand back a URL. Nothing here
// needs the icon inlined: all of them are rendered without props, none is
// recoloured through `currentColor`, and each SVG carries its own width and
// height, so an <img> draws at exactly the size the component did.
//
// Keep new icons on this pattern unless one genuinely needs to be styled from
// CSS, and give that one an inline component of its own rather than reaching
// for the query again.
function makeIcon({
  src, width, height, name,
}) {
  // Defaults are parameter defaults, not `Icon.defaultProps`: React 19 ignores
  // defaultProps on a function component and warns about it.
  function Icon({ className = undefined, alt = '' }) {
    return (
      <img
        src={src}
        width={width}
        height={height}
        className={className}
        alt={alt}
      />
    );
  }

  Icon.displayName = name;
  Icon.propTypes = {
    className: PropTypes.string,
    // Empty by default: every one of these sits beside the text that names it,
    // so a screen reader announcing it twice is worse than not at all.
    alt: PropTypes.string,
  };

  return Icon;
}

// Sizes are the SVGs' own root width/height, so the rendered box is unchanged.
const CopySVG = makeIcon({
  src: copySrc, width: 30, height: 30, name: 'CopySVG',
});
const DownloadSVG = makeIcon({
  src: downloadSrc, width: 30, height: 30, name: 'DownloadSVG',
});
const PaidSVG = makeIcon({
  src: paidSrc, width: 24, height: 24, name: 'PaidSVG',
});
const PdfSVG = makeIcon({
  src: pdfSrc, width: 36, height: 36, name: 'PdfSVG',
});
const SuccessSVG = makeIcon({
  src: successSrc, width: 140, height: 140, name: 'SuccessSVG',
});

export {
  CopySVG,
  DownloadSVG,
  PaidSVG,
  PdfSVG,
  SuccessSVG,
};
